import type { ReactNode } from 'react'
import { useRef } from 'react'
import { useDirectionalIndicator } from '../../hooks/use-directional-indicator'
import { cn } from '../../lib/utils'

/** One choice in a `SegmentedControl`. */
export type Segment<T extends string> = { value: T; label: ReactNode; disabled?: boolean }

export type SegmentedControlProps<T extends string> = {
  options: Segment<T>[]
  /** `null` presses nothing. A module holding two scopes at once is that case. */
  value: T | null
  onValueChange: (value: T) => void
  /** Required. The group is a toolbar of toggle buttons, so it needs a name. */
  ariaLabel: string
  /** Disables every segment. A single segment disables itself through `Segment`. */
  disabled?: boolean
  /** Each segment gets `data-testid={`${testIdPrefix}-${value}`}`. */
  testIdPrefix?: string
  className?: string
}

/**
 * A row of mutually exclusive choices, drawn one way for the whole product
 * (DESIGN.md §5 "Navigation": tabs versus segmented controls).
 *
 * Tabs switch between views of one place and sit on a ruled line; a
 * segmented control picks one value for what is on screen (a basis, a period,
 * a view mode) and sits in a `--muted` track. The selected segment is paper
 * with an `--input` edge, so it reads as the one raised choice, and its label
 * turns ink; the others are secondary ink. Segments have 3px corners inside
 * the track's 4px (The Radius Rule). Disabled segments are recoloured, never
 * faded.
 *
 * This is a toggle-button group, not a form control: `aria-label` on
 * `role="group"` is what the ARIA Authoring Practices give for a toolbar of
 * toggle buttons, and each segment states `aria-pressed`.
 *
 * Hook for the signature layer (lead 5): the mark moves as a stroke. The
 * group is `data-slot="segmented-control"` and `relative`; each segment is
 * `data-slot="segment"` with `data-state="on"` or `"off"`.
 */
export function SegmentedControl<T extends string>(props: SegmentedControlProps<T>) {
  const {
    options,
    value,
    onValueChange,
    ariaLabel,
    disabled = false,
    testIdPrefix,
    className,
  } = props
  const groupRef = useRef<HTMLDivElement>(null)

  useDirectionalIndicator(
    groupRef,
    value,
    '[data-slot="segment"][aria-pressed="true"]',
    'segmented',
  )

  return (
    // biome-ignore lint/a11y/useSemanticElements: a `fieldset` would need a `legend`; see the docblock.
    <div
      role="group"
      aria-label={ariaLabel}
      data-slot="segmented-control"
      ref={groupRef}
      className={cn(
        'relative isolate inline-flex gap-0.5 rounded-sm border border-border bg-muted p-0.5',
        className,
      )}
    >
      {options.map((option) => {
        const isSelected = value === option.value
        const isDisabled = disabled || option.disabled === true
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            data-slot="segment"
            data-state={isSelected ? 'on' : 'off'}
            data-testid={testIdPrefix ? `${testIdPrefix}-${option.value}` : undefined}
            disabled={isDisabled}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'relative z-10 inline-flex h-7 shrink-0 items-center justify-center gap-2 rounded-badge border px-3 text-label whitespace-nowrap transition-ui',
              'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
              "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
              isSelected
                ? 'border-input bg-card text-foreground'
                : 'border-transparent text-muted-foreground enabled:hover:text-foreground',
              isDisabled && 'cursor-not-allowed text-muted-foreground',
              'data-[signature-indicator-hidden=true]:!border-transparent data-[signature-indicator-hidden=true]:!bg-transparent',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
